/**
 * Credit Line Increase Waiting Periods by Bank
 * Based on industry standards and bank policies
 * Periods are in months
 */

const WAITING_PERIODS = {
  // Tier 1 - National Premium Banks
  'Chase Bank': 3,
  'Chase': 3,
  'American Express': 3,
  'Amex': 3,
  'Bank of America': 6,
  'Wells Fargo': 6,
  'US Bank': 6,
  'Citibank': 6,
  'Citi': 6,
  'Apple Card': 3,
  'Goldman Sachs': 3,
  'Goldman Sachs/Apple Card': 3,
  
  // Tier 2 - Regional & Strategic Banks
  'Capital One': 3,
  'Discover': 6,
  'PNC Bank': 6,
  'Truist Bank': 6,
  'Citizens Bank': 6,
  'Navy Federal Credit Union': 6,
  'PenFed Credit Union': 6,
  'TD Bank': 6,
  'Regions Bank': 6,
  'Fifth Third Bank': 6,
  'KeyBank': 6,
  'M&T Bank': 6,
  'Huntington Bank': 6,
  'BB&T': 6,
  'SunTrust': 6,
  'Ally Bank': 6,
  'USAA': 6,
  'State Farm Bank': 6,
  
  // Tier 3 - Tactical Options / Subprime
  'Synchrony Bank': 6,
  'Barclays': 6,
  'Credit One Bank': 6,
  'Indigo': 6,
  'Comenity Bank': 6,
  'First Premier Bank': 6,
  'Credit Union': 6,
  'Regional Bank': 6,
  'Store Card': 6,
  'Merrick Bank': 6,
  'Total Visa': 6,
  'Milestone': 6,
  'Surge': 6,
  'Reflex': 6,
  'Fingerhut': 6,
  'Aspire': 6,
  'OpenSky': 6,
  'Applied Bank': 6,
  'First Access': 6,
  'Fortiva': 6,
};

/**
 * Get the waiting period in months for a given bank
 * @param {string} bankName - Name of the bank
 * @returns {number} Waiting period in months (default: 6)
 */
export const getWaitingPeriod = (bankName) => {
  if (!bankName) return 6; // Default to 6 months
  
  const normalizedName = bankName.trim();
  
  // Try exact match first
  if (WAITING_PERIODS[normalizedName]) {
    return WAITING_PERIODS[normalizedName];
  }
  
  // Try case-insensitive match
  const lowerName = normalizedName.toLowerCase();
  for (const [key, value] of Object.entries(WAITING_PERIODS)) {
    if (key.toLowerCase() === lowerName) {
      return value;
    }
  }
  
  // Try partial match (e.g., "Chase" matches "Chase Bank")
  for (const [key, value] of Object.entries(WAITING_PERIODS)) {
    if (lowerName.includes(key.toLowerCase()) || key.toLowerCase().includes(lowerName)) {
      return value;
    }
  }
  
  // Default to 6 months if no match found
  return 6;
};

/**
 * Calculate the progress percentage and status for credit line increase eligibility
 * @param {Date|string|Timestamp} lastIncreaseDate - Date of last credit increase request
 * @param {Date|string|Timestamp} cardCreatedDate - Date card was created (fallback)
 * @param {number} waitingPeriodMonths - Waiting period in months
 * @returns {Object} { percentage, color, status, daysElapsed, daysRemaining, isReady }
 */
export const calculateIncreaseReadiness = (lastIncreaseDate, cardCreatedDate, waitingPeriodMonths) => {
  // Determine the reference date (last increase date or card created date)
  let referenceDate = null;
  
  if (lastIncreaseDate) {
    referenceDate = lastIncreaseDate;
  } else if (cardCreatedDate) {
    referenceDate = cardCreatedDate;
  } else {
    // No date available
    return {
      percentage: 0,
      color: '#dc3545', // Red
      status: 'unknown',
      daysElapsed: 0,
      daysRemaining: waitingPeriodMonths * 30,
      isReady: false,
    };
  }
  
  // Convert to Date object if needed
  let refDate;
  if (referenceDate.toDate) {
    refDate = referenceDate.toDate();
  } else if (typeof referenceDate === 'string') {
    refDate = new Date(referenceDate);
  } else if (referenceDate instanceof Date) {
    refDate = referenceDate;
  } else {
    refDate = new Date(referenceDate);
  }
  
  const now = new Date();
  const daysElapsed = Math.floor((now - refDate) / (1000 * 60 * 60 * 24));
  const totalDays = waitingPeriodMonths * 30; // Approximate months to days
  const daysRemaining = Math.max(0, totalDays - daysElapsed);
  
  // Calculate percentage (cap at 100%)
  const percentage = Math.min(100, Math.max(0, (daysElapsed / totalDays) * 100));
  
  // Determine color and status based on percentage
  let color, status;
  if (percentage <= 33.33) {
    color = '#dc3545'; // Red
    status = 'early';
  } else if (percentage <= 66.66) {
    color = '#fd7e14'; // Orange
    status = 'middle';
  } else if (percentage < 100) {
    color = '#ffc107'; // Yellow
    status = 'late';
  } else {
    color = '#28a745'; // Green
    status = 'ready';
  }
  
  return {
    percentage,
    color,
    status,
    daysElapsed,
    daysRemaining,
    isReady: percentage >= 100,
  };
};

